"use client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CacheKeys } from "../../constants/CacheKeys";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import {
  useGetLinkFields,
  useGetLinkQuickEditFields,
  useUpdateToolDrafts,
} from "../../hooks/useTools";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { FieldValue } from "../../models/Field";
import {
  Tool,
  ToolTypes,
  UpdateToolsMetadata,
  UpdateToolsRequest,
} from "../../models/Tool";
import { ReportType } from "../../services/ReportService";
import { mergeToolsFieldsWithAssociatedActivitiesFields } from "../../services/ToolService";
import { getToolColumnsFromColumnOrder } from "../../services/ViewService";
import {
  PaginatedList,
  PaginatedListEditingOptions,
} from "../list/PaginatedList";

const DraftLinkList = () => {
  const { t } = useTranslation();
  const { mutateAsync: updateTools, isPending: updateToolsPending } =
    useUpdateToolDrafts();

  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetLinkFields();
  const { data: quickEditViewDefinition } = useGetLinkQuickEditFields();

  const {
    data: activityViewDefinition,
    isLoading: activityViewDefinitionLoading,
  } = useGetActivityFields();

  const columns = useMemo<VsaTableColumnDefinition<Tool>[]>(() => {
    if (
      viewDefinitionLoading ||
      !viewDefinition ||
      !activityViewDefinition ||
      activityViewDefinitionLoading
    ) {
      return [];
    }

    return getToolColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      activityViewDefinition.fields,
      viewDefinition.draftsFieldOrder,
      ToolTypes.Link,
      true
    );
  }, [
    viewDefinition,
    viewDefinitionLoading,
    activityViewDefinition,
    activityViewDefinitionLoading,
  ]);

  const mapToolToRequest = (tool: Tool): UpdateToolsMetadata => {
    if (!quickEditViewDefinition?.fields) {
      throw Error("Tool fields are undefined.");
    }

    const newFieldsWithValues: Record<string, FieldValue> = {};

    if (tool.fieldValues) {
      quickEditViewDefinition.fields.forEach((field) => {
        if (field.type === 0) {
          const tagFieldValue = tool.fieldValues?.tags[field.uniqueName];

          if (tagFieldValue) {
            newFieldsWithValues[field.uniqueName] = tagFieldValue.map(
              (tag) => tag.id
            );
          }
        } else if (field.type === 3) {
          const userFieldValue = tool.fieldValues?.users[field.uniqueName];

          if (userFieldValue) {
            if (
              Array.isArray(userFieldValue) &&
              typeof userFieldValue[0] === "string"
            ) {
              newFieldsWithValues[field.uniqueName] = userFieldValue;
            } else {
              newFieldsWithValues[field.uniqueName] = (
                userFieldValue as { id: string }[]
              )
                .map((u) => u.id)
                .filter(Boolean);
            }
          }
        } else {
          const booleanFieldValue =
            tool.fieldValues?.booleans[field.uniqueName];

          if (booleanFieldValue !== undefined && booleanFieldValue !== null) {
            newFieldsWithValues[field.uniqueName] = booleanFieldValue;
          }
        }
      });
    }

    return {
      versionId: tool.versionId,
      title: tool.title,
      webUrl: tool.webUrl,
      fieldsWithValues: newFieldsWithValues,
    };
  };

  const onClickSave = async (toolsToSave: Tool[]) => {
    const request: UpdateToolsRequest = {
      toolMetadata: toolsToSave.map(mapToolToRequest),
    };

    await updateTools(request);
  };

  const editOptions: PaginatedListEditingOptions<Tool> = {
    createFormRoute: `${ClientRoutes.ToolCreate}?toolType=Link`,
    allowQuickEdit: true,
    onSave: onClickSave,
    savePending: updateToolsPending,
    viewDefinition: quickEditViewDefinition,
    entityType: "tool",
    toolType: ToolTypes.Link,
  };

  return (
    <PaginatedList<Tool>
      apiEndpoint={ApiEndpoints.ToolLinkDrafts}
      columns={columns}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      searchPlaceholder={t("searchTool")}
      cacheKey={CacheKeys.Link.LinkDraftList}
      filtersEndpoint={ApiEndpoints.ToolLinkFilters}
      editingOptions={editOptions}
      postQueryTransformation={mergeToolsFieldsWithAssociatedActivitiesFields}
      reportType={ReportType.DraftLinks}
      storageKey="links:drafts"
      libraryFilterScope={{ listType: "link" }}
      selectionPersistenceKey={SelectionPersistenceKeys.LinksDrafts}
    />
  );
};

export default DraftLinkList;
